// src/types/index.ts

// ── User Roles (Section 8.2) ─────────────────────────────────
export type UserRole =
  | "mrn_admin"
  | "retailer_admin"
  | "retailer_analyst"
  | "brand_manager"
  | "brand_analyst"
  | "agency_user";

export const ROLE_LABELS: Record<UserRole, string> = {
  mrn_admin:        "MRN Admin",
  retailer_admin:   "Retailer Admin",
  retailer_analyst: "Retailer Analyst",
  brand_manager:    "Brand Manager",
  brand_analyst:    "Brand Analyst",
  agency_user:      "Agency User",
};

// ── Auth ─────────────────────────────────────────────────────
export interface User {
  id:           string;
  name:         string;
  email:        string;
  role:         UserRole;
  orgName:      string;   // retailer name, brand name, or agency name
  orgId:        string;
  avatarInitials: string;
}

// ── Navigation ───────────────────────────────────────────────
export type ModuleId =
  | "dashboard"
  | "audiences"
  | "campaigns"
  | "intelligence"
  | "reporting"
  | "partners";

export interface NavItem {
  id:       ModuleId;
  label:    string;
  path:     string;
  icon:     string;   // lucide icon name
  roles:    UserRole[];  // which roles can see this item
  badge?:   string;      // optional badge text (e.g. "New")
}

// ── Shared primitives ────────────────────────────────────────
export type CampaignStatus  = "draft" | "active" | "paused" | "completed";
export type CampaignObjective = "awareness" | "consideration" | "conversion" | "retention";
export type AttributionModel  = "last_touch" | "first_touch" | "linear" | "time_decay" | "data_driven";
export type Channel = "oo" | "display" | "ctv" | "video" | "social";
export type RefreshSchedule = "real_time" | "daily" | "weekly" | "manual";

// ── KPI card ─────────────────────────────────────────────────
export interface KpiMetric {
  label:    string;
  value:    string | number;
  delta?:   number;    // % change, positive = up, negative = down
  prefix?:  string;    // e.g. "$"
  suffix?:  string;    // e.g. "x" for ROAS
  format?:  "number" | "currency" | "percent" | "multiplier";
}

// ── Activity feed ────────────────────────────────────────────
export type ActivityType =
  | "campaign_launch"
  | "audience_created"
  | "report_ready"
  | "lapse_alert"
  | "intent_surge"
  | "budget_warning"
  | "partner_onboarded";

export interface ActivityItem {
  id:        string;
  type:      ActivityType;
  title:     string;
  subtitle:  string;
  timestamp: string;   // ISO date string
  severity?: "info" | "warning" | "success" | "error";
}

// ── Alert ────────────────────────────────────────────────────
export interface Alert {
  id:       string;
  type:     "lapse_risk" | "intent_surge" | "budget_pacing" | "data_freshness";
  title:    string;
  message:  string;
  severity: "info" | "warning" | "error";
  cta?:     string;
  ctaPath?: string;
}

// ── Retailer / Brand ─────────────────────────────────────────
export interface Retailer {
  id:       string;
  name:     string;
  vertical: string;
  loyaltyMembers: number;
  matchRate: number;
}

export interface Brand {
  id:       string;
  name:     string;
  vertical: string;
  activeCampaigns: number;
  totalSpend: number;
}

// ── Campaign ─────────────────────────────────────────────────
export interface Campaign {
  id:          string;
  name:        string;
  advertiser:  string;
  advertiserId: string;
  objective:   CampaignObjective;
  status:      CampaignStatus;
  channels:    Channel[];
  budget:      number;
  spent:       number;
  impressions: number;
  clicks:      number;
  conversions: number;
  roas:        number;
  startDate:   string;
  endDate:     string;
  audienceSize: number;
  attributionModel: AttributionModel;
}

// ── Audience Segment ─────────────────────────────────────────
export interface AudienceSegment {
  id:               string;
  name:             string;
  estimatedSize:    number;
  filters:          string[];   // human-readable filter descriptions
  refreshSchedule:  RefreshSchedule;
  channels:         Channel[];
  createdAt:        string;
  updatedAt:        string;
  status:           "active" | "draft" | "archived";
}

// ── Intent Topic ─────────────────────────────────────────────
export interface IntentTopic {
  id:          string;
  category:    string;
  subcategory: string;
  topic:       string;
  iabCode:     string;
  count7d:     number;
  count30d:    number;
  trend:       "rising" | "declining" | "stable";
}

// ── Intent Category ──────────────────────────────────────────
export interface IntentCategory {
  category:  string;
  topics:    number;
  volume7d:  number;
  volume30d: number;
  color:     string;
}

// ── SKU / Product ────────────────────────────────────────────
export interface Product {
  sku:         string;
  name:        string;
  brand:       string;
  category:    string;
  price:       number;
  affinityScore: number;   // 0-100
  intentTopics: string[];
  trending:    boolean;
}

// ── Attribution Journey ──────────────────────────────────────
export interface TouchPoint {
  channel:     Channel;
  impressions: number;
  clicks:      number;
  conversions: number;
  spend:       number;
  credit:      number;   // attribution credit 0-1
}